import { useEffect, useState } from 'react';
import api from '../../api/axios';
import StatusBadge from '../../components/StatusBadge';
import StatusFilter from '../../components/StatusFilter';
import RequestFormModal from '../../components/RequestFormModal';
import { toast } from 'react-toastify';

const DECISION_STYLE = {
  approved: { dot: '#4f6ef7', bg: '#eef2ff', label: 'Approved' },
  rejected: { dot: '#dc2626', bg: '#fee2e2', label: 'Rejected' },
  pending:  { dot: '#f59e0b', bg: '#fef3c7', label: 'Waiting' },
};

const AllRequests = ({ source }) => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [modalReq, setModalReq] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => api.get('/requests/all').then(r => {
    const all = r.data;
    setRequests(source ? all.filter(x => x.source === source) : all);
  });
  useEffect(() => { load(); }, [source]);

  const handleDecision = async status => {
    if (!modalReq) return;
    setLoading(true);
    try {
      const roll = await api.patch(`/requests/${modalReq.id}/review`, { status, source: modalReq.source });
      const done = roll.data.approved === roll.data.total;
      toast.success(
        status === 'rejected'
          ? 'Request rejected'
          : done
            ? `All ${roll.data.total} approvers signed off — sent to IT`
            : `Approved. Waiting on ${roll.data.total - roll.data.approved} more approver(s).`
      );
      setModalReq(null);
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const approvals = selected?.approvals || [];
  const approvedCount = approvals.filter(a => a.decision === 'approved').length;

  const pageTitle = source === 'material_requests' ? 'Material Requests'
    : source === 'vendor_requests' ? 'Vendor Requests'
    : 'All Requests';

  return (
    <div>
      <div style={S.banner}>
        <div style={S.bannerBg} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={S.bannerTitle}>{pageTitle}</div>
          <div style={S.bannerSub}>Filter and view requests across the system</div>
        </div>
        <div style={S.blob} />
      </div>

      <div style={S.layout}>
        {/* ── Table ── */}
        <div style={S.tableCard}>
          <div style={S.cardTop}>
            <StatusFilter value={filter} onChange={v => { setFilter(v); setSelected(null); }} requests={requests} />
            <div style={S.badge}>{filtered.length} records</div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr style={S.thead}>
                  {['Request #', 'Title', 'Category', 'Requested By', 'Status', 'Date'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={6} style={S.empty}>No requests found</td></tr>}
                {filtered.map((r, i) => {
                  const active = selected?.source === r.source && selected?.id === r.id;
                  return (
                    <tr key={`${r.source}-${r.id}`}
                      style={{ ...S.tr, background: active ? '#f0f4ff' : i % 2 === 0 ? '#fff' : '#f8faff', cursor: 'pointer' }}
                      onClick={() => setSelected(r)}>
                      <td style={{ ...S.td, fontWeight: 700, color: '#4f6ef7' }}>
                        <span onClick={e => { e.stopPropagation(); setModalReq(r); }} style={S.reqLink}>{r.request_number}</span>
                      </td>
                      <td style={{ ...S.td, fontWeight: 600, color: '#0B1554' }}>{r.title}</td>
                      <td style={S.td}><span style={S.catBadge}>{r.category}</span></td>
                      <td style={S.td}>{r.requester_name}</td>
                      <td style={S.td}><StatusBadge status={r.status} /></td>
                      <td style={{ ...S.td, color: '#94a3b8' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Side Panel ── */}
        {selected && (
          <div style={S.panel}>
            <div style={S.panelHead}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.panelReqNum}>{selected.request_number}</div>
                <div style={S.panelMeta}>{selected.category} · {selected.requester_name}</div>
              </div>
              <button onClick={() => setSelected(null)} style={S.closeBtn}>✕</button>
            </div>

            <div style={S.panelScroll}>
              <div style={S.strip}>
                <div style={S.stripItem}>
                  <span style={S.stripLabel}>Status</span>
                  <StatusBadge status={selected.status} />
                </div>
                <div style={S.stripItem}>
                  <span style={S.stripLabel}>Date</span>
                  <span style={S.stripVal}>{new Date(selected.created_at).toLocaleDateString()}</span>
                </div>
                <div style={S.stripItem}>
                  <span style={S.stripLabel}>Approvals</span>
                   <span style={{ ...S.stripVal, color: approvedCount === approvals.length && approvals.length > 0 ? '#4f6ef7' : '#f59e0b', fontWeight: 800 }}>

                    {approvedCount}/{approvals.length}
                  </span>
                </div>
              </div>

              <div style={S.section}>
                <div style={S.sectionLabel}>Request</div>
                <div style={S.bigTitle}>{selected.title}</div>
                {selected.description && <div style={S.desc}>{selected.description}</div>}
              </div>

              {approvals.length > 0 && (
                <div style={S.section}>
                  <div style={S.sectionLabel}>Approval Chain</div>
                  <div style={S.progressTrack}>
                     <div style={{ ...S.progressFill, width: `${(approvedCount / approvals.length) * 100}%`, background: approvedCount === approvals.length ? '#4f6ef7' : '#4f6ef7' }} />

                  </div>
                  <div style={S.timeline}>
                    {approvals.map((a, i) => {
                      const ds = DECISION_STYLE[a.decision] || DECISION_STYLE.pending;
                      return (
                        <div key={i} style={S.tlRow}>
                          <div style={S.tlLeft}>
                            <div style={{ ...S.tlDot, background: ds.dot, boxShadow: `0 0 0 3px ${ds.bg}` }} />
                            {i < approvals.length - 1 && <div style={S.tlLine} />}
                          </div>
                          <div style={S.tlBody}>
                            <div style={S.tlName}>{a.approver_name}</div>
                            <div style={S.tlEmail}>{a.approver_email}</div>
                            <span style={{ ...S.tlBadge, background: ds.bg, color: ds.dot }}>{ds.label}</span>
                            {a.remarks && <div style={S.tlRemarks}>"{a.remarks}"</div>}
                            {a.decided_at && <div style={S.tlDate}>{new Date(a.decided_at).toLocaleString()}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selected.approver_remarks && (
                <div style={S.section}>
                  <div style={S.sectionLabel}>Approver Remarks</div>
                  <div style={S.remarkBox}>{selected.approver_remarks}</div>
                </div>
              )}

              {['approved', 'in_progress', 'completed'].includes(selected.status) && (
                <div style={S.section}>
                  <div style={S.sectionLabel}>IT Processing</div>
                  <div style={{ ...S.itBox, borderColor: selected.status === 'completed' ? '#bbf7d0' : '#fed7aa', background: selected.status === 'completed' ? '#f0fdf4' : '#fff7ed' }}>
                    <div style={S.itRow}>
                       <span style={{ ...S.itDot, background: selected.status === 'completed' ? '#4f6ef7' : selected.status === 'in_progress' ? '#f59e0b' : '#94a3b8' }} />

                      <span style={S.itStatusText}>{selected.status === 'completed' ? 'Completed' : selected.status === 'in_progress' ? 'In Progress' : 'Awaiting IT'}</span>
                      {selected.reviewer_name && <span style={S.itAssigned}>· {selected.reviewer_name}</span>}
                    </div>
                    {selected.it_remarks && <div style={S.itRemarks}>{selected.it_remarks}</div>}
                    {selected.material_number && <div style={S.itCode}>Material #: <strong>{selected.material_number}</strong></div>}
                    {selected.business_partner_number && <div style={S.itCode}>BP #: <strong>{selected.business_partner_number}</strong></div>}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {modalReq && (
        <RequestFormModal
          request={modalReq}
          onClose={() => setModalReq(null)}
          actions={modalReq.status === 'pending' ? (
            <>
              <button style={{ ...S.actionBtn, ...S.actionApprove }} onClick={() => handleDecision('approved')} disabled={loading}>✓ Approve</button>
              <button style={{ ...S.actionBtn, ...S.actionReject }} onClick={() => handleDecision('rejected')} disabled={loading}>✕ Reject</button>
            </>
          ) : null}
        />
      )}
    </div>
  );
};

const S = {
  banner: { background: 'linear-gradient(135deg,#060d3a 0%,#0d1a6e 50%,#2d0a5a 100%)', borderRadius: 18, marginBottom: 24, padding: '32px 36px', position: 'relative', overflow: 'hidden', boxShadow: '0 8px 32px rgba(6,13,58,0.25)' },
  bannerBg: { position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 80% 50%,rgba(124,58,237,0.2) 0%,transparent 60%)' },
  bannerTitle: { color: '#fff', fontSize: 24, fontWeight: 800, marginBottom: 8, letterSpacing: -0.3 },
  bannerSub: { color: 'rgba(255,255,255,0.6)', fontSize: 14 },
  blob: { position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: 'rgba(124,58,237,0.12)', top: -80, right: 60, filter: 'blur(30px)' },
  layout: { display: 'flex', gap: 20, alignItems: 'flex-start' },
  tableCard: { flex: 2, background: '#fff', borderRadius: 18, boxShadow: '0 4px 20px rgba(11,21,84,0.06)', overflow: 'hidden', minWidth: 0, border: '1px solid rgba(79,110,247,0.06)' },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 12 },
  badge: { background: '#f0f4ff', color: '#4f6ef7', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, whiteSpace: 'nowrap' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'linear-gradient(135deg,#060d3a,#0d1a6e)' },
  th: { padding: '13px 18px', color: '#F7D506', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: 0.8 },
  tr: { borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' },
  td: { padding: '13px 18px', fontSize: 13.5, color: '#374151' },
  empty: { textAlign: 'center', padding: '40px 20px', color: '#94a3b8', fontSize: 14 },
  catBadge: { background: '#f0f4ff', color: '#4f6ef7', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6 },
  reqLink: { cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', color: '#4f6ef7' },
  panel: { width: 420, flexShrink: 0, background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px rgba(11,21,84,0.12)', overflow: 'hidden', border: '1px solid rgba(79,110,247,0.08)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' },
  panelHead: { background: 'linear-gradient(135deg,#060d3a,#0d1a6e)', padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0 },
  panelReqNum: { color: '#F7D506', fontWeight: 800, fontSize: 14, marginBottom: 2 },
  panelMeta: { color: 'rgba(255,255,255,0.6)', fontSize: 12 },
  closeBtn: { background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', width: 28, height: 28, borderRadius: 7, fontSize: 14, fontWeight: 700, cursor: 'pointer', flexShrink: 0 },
  panelScroll: { overflowY: 'auto', flex: 1 },
  strip: { display: 'flex', borderBottom: '1px solid #f1f5f9', padding: '12px 20px' },
  stripItem: { flex: 1, display: 'flex', flexDirection: 'column', gap: 4, padding: '0 8px', borderRight: '1px solid #f1f5f9' },
  stripLabel: { fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.7, textTransform: 'uppercase' },
  stripVal: { fontSize: 13, fontWeight: 700, color: '#0B1554' },
  section: { padding: '14px 20px', borderBottom: '1px solid #f8faff' },
  sectionLabel: { fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 },
  bigTitle: { fontSize: 14, fontWeight: 700, color: '#0B1554', marginBottom: 4, lineHeight: 1.4 },
  desc: { fontSize: 12.5, color: '#64748b', lineHeight: 1.6 },
  progressTrack: { height: 5, borderRadius: 3, background: '#e2e8f0', overflow: 'hidden', marginBottom: 14 },
  progressFill: { height: '100%', borderRadius: 3, transition: 'width 0.4s' },
  timeline: { display: 'flex', flexDirection: 'column' },
  tlRow: { display: 'flex', gap: 12, marginBottom: 4 },
  tlLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16, flexShrink: 0 },
  tlDot: { width: 12, height: 12, borderRadius: '50%', flexShrink: 0, marginTop: 3 },
  tlLine: { width: 2, flex: 1, background: '#e2e8f0', margin: '4px 0' },
  tlBody: { flex: 1, paddingBottom: 12 },
  tlName: { fontSize: 13, fontWeight: 700, color: '#0B1554' },
  tlEmail: { fontSize: 11.5, color: '#64748b', marginBottom: 4 },
  tlBadge: { display: 'inline-block', fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 20, letterSpacing: 0.3 },
  tlRemarks: { fontSize: 12, color: '#475569', fontStyle: 'italic', marginTop: 5, lineHeight: 1.5 },
  tlDate: { fontSize: 10.5, color: '#94a3b8', marginTop: 3 },
  remarkBox: { fontSize: 13, color: '#374151', background: '#f8faff', border: '1px solid #e5e9f5', borderRadius: 10, padding: '10px 12px', lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  itBox: { border: '1px solid', borderRadius: 10, padding: '10px 14px' },
  itRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
  itDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  itStatusText: { fontSize: 13, fontWeight: 700, color: '#0B1554' },
  itAssigned: { fontSize: 12, color: '#64748b' },
  itRemarks: { fontSize: 12.5, color: '#374151', lineHeight: 1.5, marginBottom: 4 },
  itCode: { fontSize: 12.5, color: '#374151', marginTop: 4 },
  actionBtn: { padding: '11px 26px', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13.5, fontWeight: 700, cursor: 'pointer' },
  actionApprove: { background: 'linear-gradient(135deg,#0d1a6e,#4f6ef7)', boxShadow: '0 4px 14px rgba(79,110,247,0.4)' },
  actionReject: { background: 'linear-gradient(135deg,#dc2626,#ef4444)', boxShadow: '0 4px 14px rgba(220,38,38,0.4)' },
};

export default AllRequests;
